import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";

export type ToastVariant = "success" | "error" | "warning" | "info" | "reward";

export type ToastOptions = {
  variant?: ToastVariant | string;
  duration?: number;
  persistent?: boolean;
  icon?: string;
  title?: string;
  haptic?: string | number[];
  onClick?: () => void;
  onDismiss?: () => void;
};

export type WMUToastAPI = {
  (message: string, variantOrOptions?: string | ToastOptions, maybeOptions?: ToastOptions): HTMLElement | null;
  show: (message: string, options?: ToastOptions) => HTMLElement | null;
  success: (message: string, options?: ToastOptions) => HTMLElement | null;
  error: (message: string, options?: ToastOptions) => HTMLElement | null;
  warning: (message: string, options?: ToastOptions) => HTMLElement | null;
  info: (message: string, options?: ToastOptions) => HTMLElement | null;
  reward: (message: string, options?: ToastOptions) => HTMLElement | null;
};

function normalizeVariant(raw: unknown): ToastVariant {
  const value = String(raw || "").trim().toLowerCase();
  if (value === "success" || value === "error" || value === "warning" || value === "info" || value === "reward") {
    return value;
  }
  return "info";
}

function defaultIcon(variant: ToastVariant): string {
  switch (variant) {
    case "success":
      return "✓";
    case "error":
      return "✕";
    case "warning":
      return "⚠";
    case "reward":
      return "✦";
    default:
      return "i";
  }
}

function ensureContainer(): HTMLElement {
  const existing = document.getElementById("toast-region");
  if (existing instanceof HTMLElement) {
    existing.classList.add("wm-toast-host", "ops-toast-root");
    existing.setAttribute("role", "status");
    existing.setAttribute("aria-live", "polite");
    existing.setAttribute("aria-atomic", "true");
    return existing;
  }
  const root = document.createElement("div");
  root.id = "toast-region";
  root.className = "wm-toast-host ops-toast-root";
  root.setAttribute("role", "status");
  root.setAttribute("aria-live", "polite");
  root.setAttribute("aria-atomic", "true");
  document.body.appendChild(root);
  return root;
}

function celebrate(variant: ToastVariant): void {
  if (variant === "reward" && typeof window.WMUFeedback?.playVisual === "function") {
    window.WMUFeedback.playVisual("achievement");
  }
}

function applyHaptic(variant: ToastVariant, haptic: unknown): void {
  const api = window.WMUHaptic;
  if (!api) {
    return;
  }
  if (typeof haptic === "string" && haptic.trim() && typeof api.play === "function") {
    api.play(haptic.trim().toLowerCase());
    return;
  }
  if (Array.isArray(haptic) && typeof api.custom === "function") {
    api.custom(haptic.filter((value): value is number => Number.isFinite(value)));
    return;
  }
  if (typeof api.play !== "function") {
    return;
  }
  if (variant === "success") api.play("success");
  if (variant === "error") api.play("warning");
  if (variant === "reward") api.play("celebration");
}

function buildToastNode(message: string, variant: ToastVariant, options: ToastOptions): HTMLElement {
  const node = document.createElement("div");
  node.className = "wm-toast-body";

  const icon = document.createElement("span");
  icon.className = "wm-toast-icon";
  icon.setAttribute("aria-hidden", "true");
  icon.textContent = String(options.icon || defaultIcon(variant));

  const body = document.createElement("span");
  body.className = "wm-toast-copy";

  const titleText = String(options.title || "").trim();
  if (titleText) {
    const title = document.createElement("strong");
    title.className = "wm-toast-title";
    title.textContent = titleText;
    body.appendChild(title);
  }

  const text = document.createElement("span");
  text.className = "wm-toast-text";
  text.textContent = message;
  body.appendChild(text);

  node.appendChild(icon);
  node.appendChild(body);
  return node;
}

function show(message: string, options: ToastOptions = {}): HTMLElement | null {
  const text = String(message || "").trim();
  if (!text) {
    return null;
  }

  const variant = normalizeVariant(options.variant);
  const duration = Number.isFinite(Number(options.duration)) ? Number(options.duration) : 3200;
  const persistent = options.persistent === true || duration <= 0;
  const container = ensureContainer();
  const toast = Toastify({
    selector: container,
    oldestFirst: true,
    duration: persistent ? 0 : Math.max(1200, duration),
    gravity: "bottom",
    position: "center",
    stopOnFocus: true,
    close: true,
    className: `wm-toast wm-toast-${variant}${persistent ? " wm-toast-persistent" : ""}`,
    ariaLive: variant === "error" ? "assertive" : "polite",
    node: buildToastNode(text, variant, options),
    onClick: () => {
      if (typeof options.onClick === "function") {
        options.onClick();
      }
    },
    callback: () => {
      if (typeof options.onDismiss === "function") {
        options.onDismiss();
      }
    },
  }).showToast();

  applyHaptic(variant, options.haptic);
  celebrate(variant);

  return toast.toastElement instanceof HTMLElement ? toast.toastElement : null;
}

function eventDetail(event: Event): Record<string, unknown> {
  if ("detail" in event && event.detail && typeof event.detail === "object") {
    return event.detail as Record<string, unknown>;
  }
  return {};
}

function showFromDetail(detail: Record<string, unknown>, fallbackVariant: ToastVariant): void {
  const message = String(detail.message || detail.text || "").trim();
  if (!message) {
    return;
  }
  show(message, {
    variant: normalizeVariant(detail.variant || detail.type || fallbackVariant),
    duration: Number(detail.duration || 0) || undefined,
    persistent: detail.persistent === true,
    icon: typeof detail.icon === "string" ? detail.icon : undefined,
    title: typeof detail.title === "string" ? detail.title : undefined,
    haptic: typeof detail.haptic === "string" || Array.isArray(detail.haptic) ? detail.haptic : undefined,
  });
}

function consumeURLFlash(): void {
  const params = new URLSearchParams(window.location.search);
  const message = String(params.get("flash") || "").trim();
  if (!message) {
    return;
  }
  show(message, { variant: normalizeVariant(params.get("flash_type") || "success") });
  params.delete("flash");
  params.delete("flash_type");
  const nextQuery = params.toString();
  const nextURL = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash || ""}`;
  window.history.replaceState({}, document.title, nextURL);
}

function boot(): void {
  ensureContainer();
  consumeURLFlash();
}

function invoke(
  message: string,
  variantOrOptions?: string | ToastOptions,
  maybeOptions?: ToastOptions,
): HTMLElement | null {
  if (variantOrOptions && typeof variantOrOptions === "object" && !Array.isArray(variantOrOptions)) {
    return show(message, variantOrOptions as ToastOptions);
  }
  const options = maybeOptions && typeof maybeOptions === "object" ? maybeOptions : {};
  return show(message, {
    ...options,
    variant: normalizeVariant(variantOrOptions || options.variant),
  });
}

invoke.show = (message: string, options?: Record<string, unknown>) => show(message, options as ToastOptions);
invoke.success = (message: string, options?: Record<string, unknown>) => show(message, { ...(options || {}), variant: "success" });
invoke.error = (message: string, options?: Record<string, unknown>) => show(message, { ...(options || {}), variant: "error" });
invoke.warning = (message: string, options?: Record<string, unknown>) => show(message, { ...(options || {}), variant: "warning" });
invoke.info = (message: string, options?: Record<string, unknown>) => show(message, { ...(options || {}), variant: "info" });
invoke.reward = (message: string, options?: Record<string, unknown>) => show(message, {
  ...(options || {}),
  variant: "reward",
  haptic: "celebration",
});

if (!window.__WMU_TOAST_BOOTED) {
  window.__WMU_TOAST_BOOTED = true;
  window.WMUToast = invoke as WMUToastAPI;
  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("flash", (event) => showFromDetail(eventDetail(event), "success"));
  document.addEventListener("wmu:flash", (event) => showFromDetail(eventDetail(event), "info"));
  document.addEventListener("wmu:toast", (event) => showFromDetail(eventDetail(event), "info"));
  if (document.readyState !== "loading") {
    boot();
  }
}
